import { useState, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faUser,
  faPhone,
  faEnvelope,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { Input } from "../../../components/ui/input";
import Modal from "../../../components/ui/modal";
import { userService } from "../../../services/service";
import { toastSuccess, toastError } from "../../../utils/toast";

const EditDetailsModal = ({
  customer,
  isOpen,
  onClose,
  onSave,
  onUserUpdated,
}) => {
  const [form, setForm] = useState({
    full_name: "",
    phone_number: "",
    email: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (customer) {
      const phoneNumber = customer.phone_number || "";
      const extractedDigits = phoneNumber.startsWith("+255")
        ? phoneNumber.substring(4)
        : phoneNumber;

      setForm({
        full_name: customer.full_name || "",
        phone_number: extractedDigits,
        email: customer.email || "",
      });
      setErrors({});
    }
  }, [customer]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone_number") {
      const cleaned = value.replace(/\D/g, "");
      const limited = cleaned.slice(0, 9);
      setForm((prev) => ({ ...prev, [name]: limited }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validatePhoneNumber = (phone) => {
    return /^[0-9]{9}$/.test(phone);
  };

  const formatPhoneNumber = (phone) => {
    return `+255${phone}`;
  };

  const handleSave = async () => {
    const newErrors = {};

    if (!form.full_name) {
      newErrors.full_name = "Full name is required";
    }

    if (!form.phone_number) {
      newErrors.phone_number = "Phone number is required";
    } else if (!validatePhoneNumber(form.phone_number)) {
      newErrors.phone_number =
        "Please enter exactly 9 digits (e.g., 712345678)";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const updateData = {
      full_name: form.full_name,
      phone: formatPhoneNumber(form.phone_number),
    };

    try {
      setIsLoading(true);
      const response = await userService.update(customer.userId, updateData);

      if (response?.status === "success") {
        toastSuccess("Customer updated successfully!");

        const updatedCustomer = {
          ...customer,
          full_name: form.full_name,
          phone_number: formatPhoneNumber(form.phone_number),
          updated_at: new Date().toISOString().split("T")[0],
        };

        onClose();

        if (onUserUpdated) {
          await onUserUpdated();
        }

        if (onSave) {
          onSave(updatedCustomer);
        }
      }
    } catch (error) {
      console.error("Failed to update customer:", error);
      toastError(
        error?.response?.data?.message ||
          "Failed to update customer. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit customer details"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading}
            className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                Save changes
              </>
            )}
          </Button>
        </div>
      }
    >
      {customer && (
        <div className="flex flex-col gap-4 text-sm">
          <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <FontAwesomeIcon icon={faUser} className="text-blue-800 h-4 w-4" />
            </div>
            <div>
              <p className="text-sm text-orange-600 font-semibold">Editing customer</p>
              <p className="font-medium text-gray-900">{customer.full_name}</p>
              <p className="text-xs text-gray-600">{customer.customer_no}</p>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-gray-700 font-medium flex items-center gap-2">
              <FontAwesomeIcon icon={faUser} className="text-blue-800 h-3.5 w-3.5" />
              Full Name *
            </label>
            <Input
              name="full_name"
              value={form.full_name}
              onChange={handleChange}
              placeholder="Enter full name"
              disabled={isLoading}
              className={`border ${errors.full_name ? "border-red-500" : "border-gray-300"} rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
            />
            {errors.full_name && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                {errors.full_name}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-gray-700 font-medium flex items-center gap-2">
              <FontAwesomeIcon icon={faPhone} className="text-blue-800 h-3.5 w-3.5" />
              Phone Number *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <span className="text-gray-600 text-sm font-medium">+255</span>
              </div>
              <Input
                name="phone_number"
                value={form.phone_number}
                onChange={handleChange}
                placeholder="712345678"
                maxLength={9}
                disabled={isLoading}
                className={`border ${errors.phone_number ? "border-red-500" : "border-gray-300"} rounded-md pl-14 pr-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
              />
            </div>
            <p className="text-xs text-gray-600">Enter 9 digits (e.g., 712345678)</p>
            {errors.phone_number && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                {errors.phone_number}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-gray-700 font-medium flex items-center gap-2">
              <FontAwesomeIcon icon={faEnvelope} className="text-blue-800 h-3.5 w-3.5" />
              Email
            </label>
            <div className="flex h-10 w-full items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              {form.email || "No email provided"}
            </div>
            <p className="text-xs text-gray-600">
              Email cannot be changed. Contact administrator for email updates.
            </p>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default EditDetailsModal;